import type { RecipeGroupDef, RecipeParamDef } from "./recipe-groups";
import { ENDPOINT } from "../services/api-endpoint";
import { getList } from "../services/services";

type ApiStep = {
  stepIndex: number;
  groups?: Array<{
    title: string;
    params?: Array<Record<string, unknown>>;
  }>;
};

function parseParam(row: Record<string, unknown>): RecipeParamDef | null {
  const key = String(row.key ?? "").trim();
  const label = String(row.label ?? "").trim();
  if (!key || !label) return null;
  return {
    key,
    label,
    unit: row.unit == null ? undefined : String(row.unit).trim() || undefined,
    required: Boolean(row.required),
    defaultValue:
      row.defaultValue == null ? undefined : String(row.defaultValue).trim(),
    minValue: row.minValue == null ? undefined : String(row.minValue).trim(),
    maxValue: row.maxValue == null ? undefined : String(row.maxValue).trim(),
  };
}

export async function fetchRecipeProcessParameterGroups(
  workflowId: number,
): Promise<RecipeGroupDef[][]> {
  const raw = await getList(ENDPOINT.RECIPE_PROCESS_PARAMETER_GROUPS, {
    workflow_id: workflowId,
  });
  const steps = Array.isArray((raw as { steps?: ApiStep[] })?.steps)
    ? (raw as { steps: ApiStep[] }).steps
    : [];
  return steps.map((step) => {
    const groups = Array.isArray(step.groups) ? step.groups : [];
    return groups
      .map((g) => {
        const title = String(g.title ?? "").trim() || "Parameters";
        const params = (Array.isArray(g.params) ? g.params : [])
          .map((p) => parseParam(p))
          .filter((x): x is RecipeParamDef => x != null);
        return { title, params };
      })
      .filter((g) => g.params.length > 0);
  });
}

export function recipeGroupsFromPayloadLayouts(
  layouts: unknown,
): RecipeGroupDef[][] | null {
  if (!Array.isArray(layouts)) return null;
  const out: RecipeGroupDef[][] = [];
  for (const step of layouts) {
    const groupsRaw = (step as { groups?: unknown })?.groups;
    if (!Array.isArray(groupsRaw)) {
      out.push([]);
      continue;
    }
    const groups = groupsRaw
      .map((g) => {
        const title = String((g as { title?: string }).title ?? "").trim() || "Parameters";
        const params = (Array.isArray((g as { params?: unknown[] }).params)
          ? (g as { params: unknown[] }).params
          : []
        )
          .map((p) => parseParam(p as Record<string, unknown>))
          .filter((x): x is RecipeParamDef => x != null);
        return { title, params };
      })
      .filter((g) => g.params.length > 0);
    out.push(groups);
  }
  return out.length ? out : null;
}
